/* eslint-disable camelcase */
import {
  BiologicalResult,
  ResultStatus,
  ResultType,
  SignificantPoints,
} from '../pages/TestListPage/TestResultModal/BiologicalResults/BiologicalResult';
import { PipelineModelRanges } from '../api/result';

const totalCholesterol: BiologicalResult = {
  title: 'Total Cholesterol',
  significantPoints: {
    graphMin: 2,
    graphMax: 8,
    domainMin: 2.5,
    domainMax: 7,
    breakpoint: 5,
  },
  lowerIsHealthy: true,
  unit: '(mmol/L)',
};

const hdl: BiologicalResult = {
  title: 'HDL',
  significantPoints: {
    graphMin: 0,
    graphMax: 3,
    domainMin: 1,
    domainMax: 2.5,
    breakpoint: 1,
  },
  lowerIsHealthy: false,
  unit: '(mmol/L)',
};

const nonHdl: BiologicalResult = {
  title: 'Non-HDL',
  significantPoints: {
    graphMin: 2,
    graphMax: 6,
    domainMin: 2.5,
    domainMax: 5,
    breakpoint: 4,
  },
  lowerIsHealthy: true,
  unit: '(mmol/L)',
};

const ldl: BiologicalResult = {
  title: 'LDL',
  significantPoints: {
    graphMin: 0,
    graphMax: 7,
    domainMin: 0,
    domainMax: 7,
    breakpoint: 3,
  },
  lowerIsHealthy: true,
  unit: '(mmol/L)',
};

const totalHdlRatio: BiologicalResult = {
  title: 'Total Cholesterol/HDL',
  significantPoints: {
    graphMin: 2,
    graphMax: 8,
    domainMin: 2.5,
    domainMax: 7,
    breakpoint: 6,
  },
  lowerIsHealthy: true,
};

const triglycerides: BiologicalResult = {
  title: 'Triglycerides',
  significantPoints: {
    graphMin: 0,
    graphMax: 3,
    domainMin: 1,
    domainMax: 2.5,
    breakpoint: 2.3,
  },
  lowerIsHealthy: true,
  unit: '(mmol/L)',
};

export const BloodTestParamConfigs = {
  totalCholesterol,
  hdl,
  nonHdl,
  ldl,
  totalHdlRatio,
  triglycerides,
};

export const MODEL3_BIOLOGICAL_RESULTS_OVERRIDES = {
  totalCholesterol: {
    significantPoints: {
      domainMin: 2.5,
      domainMax: 7.5,
    },
  },
  triglycerides: {
    significantPoints: {
      domainMin: 1,
      domainMax: 4.5,
      graphMax: 5,
    },
  },
};

// Maps the FE camelCase resultType to the snake_case biomarker key admin-be uses in
// subject_data.pipeline_model_ranges.
const PIPELINE_RANGE_KEYS: Record<ResultType, keyof PipelineModelRanges> = {
  totalCholesterol: 'total_cholesterol',
  hdl: 'hdl',
  nonHdl: 'non_hdl',
  ldl: 'ldl',
  totalHdlRatio: 'tc_hdl_ratio',
  triglycerides: 'triglycerides',
};

// Static, model-keyed fallback overrides used ONLY when per-test ranges are absent.
// Keyed on the chip model (results.metadata.model_id) — NOT subject_data.model_id,
// which defaults to "model-id-1" and caused the original "> 7.0" defect.
const MODEL_STATIC_OVERRIDES: Record<
  string,
  Partial<Record<ResultType, { significantPoints: Partial<SignificantPoints> }>>
> = {
  'model-id-3': MODEL3_BIOLOGICAL_RESULTS_OVERRIDES,
};

// Fields we consume from per-test ranges: the limit-of-quantification caps (domainMin/
// domainMax — the subject of this ticket) plus the graph axis bounds needed to display
// the extended range (e.g. the "> 7.5" marker). `breakpoint` is intentionally excluded:
// it is the clinical Healthy/High divider, the endpoint returns one but we do NOT use it
// today, so it stays the static clinical value. Adopting it must be its own ticket.
const CONSUMED_RANGE_FIELDS: Array<keyof SignificantPoints> = [
  'domainMin',
  'domainMax',
  'graphMin',
  'graphMax',
];

/**
 * Resolves the significant-points override for a biomarker, in priority order:
 *   1. Per-test ranges from the pipeline (pipeline_model_ranges) — the single source of
 *      truth that produced the result/PDF. Model-blind by design. Only the measurement/
 *      display bounds are read (see CONSUMED_RANGE_FIELDS); breakpoint is ignored.
 *   2. Model-aware fallback keyed on the chip model — keeps model-3 correct for tests
 *      that don't (yet) carry per-test ranges (historical records, deploy skew, etc.).
 *   3. undefined — the caller (BiologicalResult) then uses the static default config.
 *
 * @param resultType biomarker being rendered
 * @param pipelineModelRanges per-test ranges from the API, if present
 * @param modelId chip model (results.metadata.model_id), used for the fallback only
 */
export const resolveSignificantPointsOverride = (
  resultType: ResultType,
  pipelineModelRanges?: PipelineModelRanges,
  modelId?: string,
): Partial<SignificantPoints> | undefined => {
  const perTest = pipelineModelRanges?.[PIPELINE_RANGE_KEYS[resultType]];
  if (perTest) {
    const override: Partial<SignificantPoints> = {};
    CONSUMED_RANGE_FIELDS.forEach((field) => {
      const value = perTest[field];
      // Only copy defined fields so we never clobber a static default with undefined.
      if (value !== undefined) {
        override[field] = value;
      }
    });
    if (Object.keys(override).length > 0) {
      return override;
    }
  }

  const modelOverride = modelId
    ? MODEL_STATIC_OVERRIDES[modelId]?.[resultType]?.significantPoints
    : undefined;
  if (modelOverride) {
    return modelOverride;
  }

  return undefined;
};

/**
 * @param min minimum value available in the domain
 * @param max maximum value available in the domain
 * @param thresholdExceeded is actual value outside the domain? value we receive here is normalized
 * to be in the domain, so this extra flag is needed to know where the actual value lies.
 * @param value value received
 * @returns value restrained to the provided domain limiters
 */

export const formatValue = (
  min: number,
  max: number,
  thresholdExceeded: boolean,
  value: number,
) => {
  if (thresholdExceeded) {
    return value === min ? `< ${min.toFixed(1)}` : `> ${max.toFixed(1)}`;
  }

  return value?.toFixed(1);
};

/**
 * For thresholded values, we provide info string which explains that the original value was
 * normalized to fit the allowed range.
 * @param min minimum value available in the domain
 * @param max maximum value available in the domain
 * @param thresholdExceeded is actual value outside the domain? value we receive here is normalized
 * to be in the domain, so this extra flag is needed to know where the actual value lies.
 * @param value value received
 * @returns info message for thresholded values, undefined otherwise
 */

export const getInfoMessage = (
  min: number,
  max: number,
  thresholdExceeded: boolean,
  value?: number,
) => {
  if (thresholdExceeded && value) {
    return value === min
      ? `Results below ${min} are shown as < ${min.toFixed(1)}`
      : `Results above ${max} are shown as > ${max.toFixed(1)}`;
  }

  return undefined;
};

/**
 * @param min minimum value available in the domain
 * @param lowerIsHealthy bool which determines if values bellow the
 * breakpoint are considered healthy
 * @param breakpoint point in the parameter domain which separates healthy and unhealthy values
 * @param thresholdExceeded is actual value outside the domain? value we receive here is normalized
 * to be in the domain, so this extra flag is needed to know where the actual value lies.
 * @param result value received from the blood test analysis
 * @returns string which determines if the value is Healthy,
 * Unhealthy (too high or too low) or not available
 */

export const getResultStatus = (
  min: number,
  lowerIsHealthy: boolean,
  breakpoint: number,
  thresholdExceeded: boolean,
  result?: number,
): ResultStatus => {
  // No result provided, status is not available
  if (result === undefined || result === null) {
    return 'N/A';
  }
  // Edge case in which min or max domain values are on the breakpoint and threshold is exceeded
  if (thresholdExceeded && result === breakpoint) {
    // if result equals min domain value, we get status for values lower than breakpoint
    if (result === min) {
      return lowerIsHealthy ? 'Healthy' : 'Low';
    }
    // otherwise we get status for values higher than breakpoint
    return lowerIsHealthy ? 'High' : 'Healthy';
  }
  if (lowerIsHealthy ? result <= breakpoint : result >= breakpoint) {
    return 'Healthy';
  }
  return lowerIsHealthy ? 'High' : 'Low';
};
